import EditIcon from "@mui/icons-material/Edit";
import { Box, Button, Divider, IconButton, Stack, SxProps, Theme, Typography } from "@mui/material";
import React, { useState } from "react";

import { DeleteIcon, OpenInFullIcon } from "assets/icons";
import { BaseImage } from "components/common/BaseImage";
import { ImageLightBox } from "components/ImageLightBox";

type EditableImageCardProps = {
  imageUrl: string;
  canModify: boolean;
  onDelete?: VoidFunction;
  onEdit?: VoidFunction;
  sx?: SxProps<Theme>;
  canViewMaximizedImage?: boolean;
};

export function EditableImageCard({
  imageUrl,
  canModify,
  onEdit,
  onDelete,
  sx,
  canViewMaximizedImage = true,
}: EditableImageCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isImageLightBoxOpen, setIsImageLightBoxOpen] = useState(false);

  return (
    <>
      <Box
        sx={sx}
        className="relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* This show an image inside a note uploaded by the user, so, no canvas required here */}
        <BaseImage width="230px">
          <img
            alt="example"
            src={imageUrl}
            className="h-full w-full object-contain object-center transition-all cursor-pointer"
          />
        </BaseImage>

        {/* Black overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "black",
            width: "100%",
            height: "100%",
            opacity: isHovered ? 0.5 : 0,
            pointerEvents: isHovered ? "auto" : "none",
            transition: "opacity 300ms ease-in-out",
          }}
        />

        <Box className={`absolute inset-0 flex items-center justify-center ${isHovered ? "inline-block" : "hidden"}`}>
          {canModify && (
            <Stack
              direction="row"
              mx="auto"
              my="auto"
              gap="20px"
              alignItems="center"
              justifyContent="center"
              divider={<Divider orientation="vertical" sx={{ bgcolor: "white", width: "1px", height: "50px" }} />}
            >
              {onDelete && (
                <Button
                  sx={{ textTransform: "none", color: "white" }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete?.();
                  }}
                >
                  <Stack alignItems="center">
                    <Typography>Delete</Typography>
                    <DeleteIcon />
                  </Stack>
                </Button>
              )}

              {onEdit && (
                <Button
                  sx={{ textTransform: "none", color: "white" }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit?.();
                  }}
                >
                  <Stack alignItems="center">
                    <Typography>Edit</Typography>
                    <EditIcon sx={{ color: "white" }} />
                  </Stack>
                </Button>
              )}
            </Stack>
          )}

          {canViewMaximizedImage && (
            <IconButton
              sx={{ position: "absolute", top: "5px", right: "5px" }}
              onClick={() => setIsImageLightBoxOpen(true)}
              aria-label="Open image in fullscreen"
            >
              <OpenInFullIcon color="white" />
            </IconButton>
          )}
        </Box>
      </Box>

      <ImageLightBox
        imageSource={imageUrl}
        isOpen={isImageLightBoxOpen}
        onClose={() => setIsImageLightBoxOpen(false)}
      />
    </>
  );
}
